import { startRegistration } from "@simplewebauthn/browser";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "../components/Icon.tsx";
import { Empty, Page, Section, useFail, useToast } from "../components/ui.tsx";
import { api, discardQueued, onQueueChange, queuedActions, replayQueue, type QueuedAction } from "../lib/api.ts";
import { label, when } from "../lib/format.ts";
import { useMe, useSignOut } from "../lib/session.tsx";
import { ThemeSwitch } from "../lib/theme.tsx";

export function More() {
  const me = useMe();
  const qc = useQueryClient();
  const toast = useToast();
  const fail = useFail();
  const signOut = useSignOut();
  const [queue, setQueue] = useState<QueuedAction[]>([]);
  useEffect(() => { const r = () => queuedActions().then(setQueue); r(); return onQueueChange(r) as () => void; }, []);
  const addPasskey = async () => {
    try {
      const opts = await api.post("/auth/passkey/register/options");
      const response = await startRegistration({ optionsJSON: opts as any });
      await api.post("/auth/passkey/register", { response, label: navigator.userAgent.includes("iPhone") ? "iPhone" : navigator.userAgent.includes("Android") ? "Android phone" : "Computer" });
      toast("Passkey added. Next time, sign in with Face ID or your fingerprint.");
      qc.invalidateQueries({ queryKey: ["me"] });
    } catch (e) { if ((e as Error).name !== "NotAllowedError") fail(e); }
  };
  const approvals = useQuery({ queryKey: ["approvals"], queryFn: () => api.get<any[]>("/approvals") });

  return (
    // The screens list lives in the side menu and the phone's Menu sheet now; this is the account page.
    <Page title="Account">
      <Section title="Drafts waiting for approval" count={approvals.data?.length ?? 0}>
        {(approvals.data ?? []).length === 0 ? <Empty>No drafts waiting.</Empty> : (
          <div className="list">{approvals.data!.slice(0, 8).map((a) => (
            <Link key={a.id} to={a.conversation_id ? `/inbox/${a.conversation_id}` : "#"} className="row">
              <div className="main-col"><div className="title">{a.title}</div><div className="sub">{a.proposed?.body}</div></div><span className="when">{when(a.created_at)}</span>
            </Link>))}</div>
        )}
        <div className="pad muted" style={{ fontSize: 13, marginTop: 8 }}>Sending is off in this build. The approval queue and sending arrive in Phase 2, and only Michael can switch a rule to automatic.</div>
      </Section>

      <Section title="Waiting to send from this phone" count={queue.length}>
        {queue.length === 0 ? <Empty>Nothing waiting. Changes you make with no signal are kept here and sent when you're back in range.</Empty> : (
          <div className="list">{queue.map((a) => (
            <div key={a.id} className="row">
              <div className="main-col"><div className="title">{a.label}</div><div className={"sub" + (a.error ? " danger" : "")}>{a.error ?? `Saved ${when(a.at)}`}</div></div>
              <button className="btn ghost sm" onClick={() => discardQueued(a.id)}>Discard</button>
            </div>))}</div>
        )}
        {queue.length > 0 && <div className="pad" style={{ marginTop: 10 }}><button className="btn sm" onClick={() => replayQueue()}>Try sending now</button></div>}
      </Section>

      <Section title="You">
        <dl className="kv">
          <dt>Name</dt><dd>{me.user.name}</dd>
          <dt>Role</dt><dd>{label(me.user.role)}</dd>
          {me.areas.length > 0 && <><dt>Area</dt><dd>{me.areas.map((a) => `${a.name} (${a.suburbs} suburbs)`).join(", ")}</dd></>}
          <dt>Passkeys</dt><dd>{me.passkeys}</dd>
          <dt>This device</dt><dd className="num">ends {me.device.id.slice(-6)}</dd>
        </dl>
        <div className="actions pad" style={{ marginTop: 12 }}>
          <button className="btn ghost" onClick={addPasskey}><Icon name="key" />Add a passkey to this device</button>
          <button className="btn ghost" onClick={signOut}><Icon name="logout" />Sign out</button>
        </div>
      </Section>

      <Section title="Appearance">
        <div className="pad"><ThemeSwitch /></div>
        <div className="pad dim" style={{ fontSize: 12.5, margin: "14px 0 24px" }}>Grassick CRM, Phase 1 local build · made-up test data · sending {me.sending}</div>
      </Section>
    </Page>
  );
}
